/**
 * BURDENS
 * Luxury Atelier Horizontal Scroll — Refined Seamless Edition
 *
 * Native vertical scrolling drives a horizontal track.
 * No wheel interception. No ResizeObserver. No text-resizing loop.
 * Default scroll multiplier: 0.9.
 */

(() => {
  const VERSION = "refined-seamless-v1";
  const SECTION_SELECTOR = "[data-atelier-section]";
  const MOBILE_BREAKPOINT = 749;
  const DEFAULT_MULTIPLIER = 0.9;
  const DEFAULT_SMOOTHNESS = 5.5;
  const DEFAULT_END_HOLD = 0.8;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const toNumber = (value, fallback) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  class LuxuryAtelierController {
    constructor(section) {
      this.section = section;
      this.sticky = section.querySelector("[data-atelier-sticky]");
      this.viewport = section.querySelector("[data-atelier-viewport]");
      this.track = section.querySelector("[data-atelier-track]");
      this.progressFill = section.querySelector(
        "[data-atelier-progress-fill]"
      );

      if (!this.sticky || !this.viewport || !this.track) {
        this.valid = false;
        return;
      }

      this.valid = true;
      this.enabled = false;
      this.destroyed = false;
      this.maxX = 0;
      this.travelY = 1;
      this.targetX = 0;
      this.currentX = 0;
      this.targetProgress = 0;
      this.currentProgress = 0;
      this.rafId = null;
      this.resizeTimer = null;
      this.lastFrame = performance.now();

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.render = this.render.bind(this);

      this.reducedMotionQuery = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );

      window.addEventListener("scroll", this.handleScroll, { passive: true });
      window.addEventListener("resize", this.handleResize, { passive: true });
      window.addEventListener("orientationchange", this.handleResize, {
        passive: true
      });

      if (this.reducedMotionQuery.addEventListener) {
        this.reducedMotionQuery.addEventListener("change", this.handleResize);
      } else if (this.reducedMotionQuery.addListener) {
        this.reducedMotionQuery.addListener(this.handleResize);
      }

      this.section.querySelectorAll("img").forEach((image) => {
        if (!image.complete) {
          image.addEventListener("load", () => this.measure(true), {
            once: true
          });
        }
      });

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (!this.destroyed) {
            this.measure(true);
          }
        });
      }

      requestAnimationFrame(() => this.measure(true));
      window.setTimeout(() => this.measure(true), 180);
    }

    readSettings() {
      this.multiplier = Math.max(
        0.1,
        toNumber(
          this.section.dataset.atelierScrollMultiplier,
          DEFAULT_MULTIPLIER
        )
      );

      this.smoothness = clamp(
        toNumber(this.section.dataset.atelierSmoothness, DEFAULT_SMOOTHNESS),
        1,
        20
      );

      this.endHold = Math.max(
        0,
        toNumber(this.section.dataset.atelierEndHold, DEFAULT_END_HOLD)
      );
    }

    shouldEnable() {
      if (this.reducedMotionQuery.matches) {
        return false;
      }

      const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      const mobileMode = this.section.dataset.atelierMobileMode || "stacked";

      return !(isMobile && mobileMode !== "horizontal");
    }

    measure(immediate = false) {
      if (!this.valid || this.destroyed) {
        return;
      }

      this.readSettings();
      this.enabled = this.shouldEnable();

      if (!this.enabled) {
        this.section.style.removeProperty("height");
        this.track.style.transform = "translate3d(0px, 0px, 0px)";

        if (this.progressFill) {
          this.progressFill.style.transform = "scaleX(0)";
        }

        this.maxX = 0;
        this.travelY = 1;
        this.targetX = 0;
        this.currentX = 0;
        this.targetProgress = 0;
        this.currentProgress = 0;
        return;
      }

      const viewportWidth = this.viewport.clientWidth;
      const viewportHeight = window.innerHeight;

      this.maxX = Math.max(0, this.track.scrollWidth - viewportWidth);
      this.travelY = Math.max(1, this.maxX * this.multiplier);

      const endingPause = viewportHeight * this.endHold;
      const sectionHeight = Math.ceil(
        viewportHeight + this.travelY + endingPause
      );

      this.section.style.height = `${sectionHeight}px`;
      this.updateTargets(immediate);
    }

    getSectionTop() {
      return window.scrollY + this.section.getBoundingClientRect().top;
    }

    updateTargets(immediate = false) {
      if (!this.enabled || this.destroyed) {
        return;
      }

      const localScroll = window.scrollY - this.getSectionTop();
      const progress = clamp(localScroll / this.travelY, 0, 1);

      this.targetProgress = progress;
      this.targetX = -this.maxX * progress;

      if (immediate) {
        this.currentProgress = this.targetProgress;
        this.currentX = this.targetX;
        this.applyTransform();
        return;
      }

      this.requestRender();
    }

    handleScroll() {
      if (!this.enabled || this.destroyed) {
        return;
      }

      const rect = this.section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (rect.bottom < -viewportHeight || rect.top > viewportHeight * 2) {
        return;
      }

      this.updateTargets(false);
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => this.measure(true), 120);
    }

    requestRender() {
      if (this.rafId || this.destroyed) {
        return;
      }

      this.lastFrame = performance.now();
      this.rafId = requestAnimationFrame(this.render);
    }

    render(now) {
      this.rafId = null;

      if (!this.enabled || this.destroyed) {
        return;
      }

      const deltaSeconds = Math.min(0.064, Math.max(0.001, (now - this.lastFrame) / 1000));
      this.lastFrame = now;

      const response = 1 - Math.exp(-this.smoothness * 3 * deltaSeconds);

      this.currentX += (this.targetX - this.currentX) * response;
      this.currentProgress +=
        (this.targetProgress - this.currentProgress) * response;

      if (Math.abs(this.targetX - this.currentX) < 0.05) {
        this.currentX = this.targetX;
      }

      if (Math.abs(this.targetProgress - this.currentProgress) < 0.0001) {
        this.currentProgress = this.targetProgress;
      }

      this.applyTransform();

      const stillMoving =
        Math.abs(this.targetX - this.currentX) >= 0.05 ||
        Math.abs(this.targetProgress - this.currentProgress) >= 0.0001;

      if (stillMoving) {
        this.rafId = requestAnimationFrame(this.render);
      }
    }

    applyTransform() {
      this.track.style.transform = `translate3d(${this.currentX.toFixed(
        3
      )}px, 0px, 0px)`;

      if (this.progressFill) {
        this.progressFill.style.transform = `scaleX(${clamp(
          this.currentProgress,
          0,
          1
        ).toFixed(5)})`;
      }
    }

    focusChapter(chapter) {
      if (!chapter || !this.enabled || this.maxX <= 0) {
        return;
      }

      const desiredX = clamp(
        chapter.offsetLeft - this.viewport.clientWidth * 0.06,
        0,
        this.maxX
      );

      const progress = desiredX / this.maxX;
      const destination = this.getSectionTop() + progress * this.travelY;

      window.scrollTo({
        top: destination,
        behavior: "smooth"
      });
    }

    destroy() {
      this.destroyed = true;

      window.removeEventListener("scroll", this.handleScroll);
      window.removeEventListener("resize", this.handleResize);
      window.removeEventListener("orientationchange", this.handleResize);

      if (this.reducedMotionQuery.removeEventListener) {
        this.reducedMotionQuery.removeEventListener(
          "change",
          this.handleResize
        );
      } else if (this.reducedMotionQuery.removeListener) {
        this.reducedMotionQuery.removeListener(this.handleResize);
      }

      window.clearTimeout(this.resizeTimer);

      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
    }
  }

  const registry =
    window.__BURDENS_ATELIER_REGISTRY__ ||
    (window.__BURDENS_ATELIER_REGISTRY__ = new Map());

  function initializeSection(section) {
    if (!section || registry.has(section)) {
      return;
    }

    const controller = new LuxuryAtelierController(section);

    if (controller.valid) {
      registry.set(section, controller);
      section.dataset.atelierControllerVersion = VERSION;
    }
  }

  function initializeAll(root = document) {
    if (root.matches && root.matches(SECTION_SELECTOR)) {
      initializeSection(root);
    }

    root.querySelectorAll(SECTION_SELECTOR).forEach(initializeSection);
  }

  function unloadSection(section) {
    const target =
      section.matches && section.matches(SECTION_SELECTOR)
        ? section
        : section.querySelector(SECTION_SELECTOR);

    if (!target) {
      return;
    }

    const controller = registry.get(target);

    if (controller) {
      controller.destroy();
      registry.delete(target);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeAll(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    unloadSection(event.target);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const section = event.target.closest(SECTION_SELECTOR);
    const chapter = event.target.closest("[data-atelier-chapter]");
    const controller = section ? registry.get(section) : null;

    if (controller && chapter) {
      controller.focusChapter(chapter);
    }
  });
})();
